// builder-ux-overhaul Phase 3 Session 1 (2026-05-22) — shared deal-header strip.
//
// Extracts the byte-identical `signal-title-strip` / `ModelHeaderStrip` pattern
// into a single presentational component so all 3 Builder tabs (Workbench,
// Model, Signal) share one composition.
//
// Key design decisions (research/phase-3.md § 4 WS1 + ADR 0006):
//   • VARIANT_* maps are the canonical home here; ModelHeaderStrip and signal
//     variants will delegate to this strip in Session 2.
//   • signalVariant===null → RIGHT cluster renders <HeroMetricsHeader> (DIV-B
//     fallback — carries the variant-agnostic "no chip" state for legacy models).
//   • showHeroReturns=false → RIGHT cluster omitted entirely (Workbench AC).
//   • pillSlot (ADR 0006) — stable LEFT-cluster wrapper; Phase 4 fills the slot.
//   • Box-shadow literals are @design-token-annotated; the component lives under
//     builder/ root (NOT builder/signal/) so audit-no-raw-hex-in-signal.sh DOES
//     NOT cover this file, but annotation kept for forward-compat.

import type { ReactNode } from 'react';
import clsx from 'clsx';
import type {
  MetricHistoryEntry,
  ModelSchema,
  SignalVariant,
} from '@arcanum-hq/shared-types';
import type { SignalFieldResolverContext } from '@/hooks/use-signal-field-resolver';
import { HeroReturns } from './signal/HeroReturns';
import { HeroMetricsHeader } from './HeroMetricsHeader';

// ---------------------------------------------------------------------------
// VARIANT_* maps — canonical home (Session 2 points existing consumers here).
// 2-value set: operating-asset | new-development.
// Verbatim from ModelHeaderStrip.tsx (research/phase-3.md § 4 WS1).
// NOTE: static Record lookups only — NO Tailwind class interpolation (F03,
// Tailwind v4 no-safelist).
// ---------------------------------------------------------------------------

export const VARIANT_LABEL: Record<Exclude<SignalVariant, null>, string> = {
  'operating-asset': 'OPERATING ASSET',
  'new-development': 'NEW DEVELOPMENT',
};

export const VARIANT_CHIP_CLASS: Record<
  Exclude<SignalVariant, null>,
  string
> = {
  'operating-asset':
    'shrink-0 px-2 py-[2px] border border-accent rounded-[2px] text-[8.5px] font-mono font-bold tracking-[1px] uppercase text-accent leading-none',
  'new-development':
    'shrink-0 px-2 py-[2px] border border-blue rounded-[2px] text-[8.5px] font-mono font-bold tracking-[1px] uppercase text-blue leading-none',
};

export const VARIANT_CHIP_STYLE: Record<
  Exclude<SignalVariant, null>,
  React.CSSProperties
> = {
  'operating-asset': {
    boxShadow:
      '0 0 8px rgba(0, 200, 83, 0.33), inset 0 0 6px rgba(0, 200, 83, 0.13)', // @design-token resolves --color-accent glow
    background: 'rgba(0, 200, 83, 0.07)', // @design-token resolves --color-accent-dim
  },
  'new-development': {
    boxShadow:
      '0 0 8px rgba(58, 120, 184, 0.33), inset 0 0 6px rgba(58, 120, 184, 0.13)', // @design-token resolves --color-blue glow
    background: 'rgba(58, 120, 184, 0.07)', // @design-token resolves --color-blue-dim
  },
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DealHeaderStripProps {
  /** h1 deal name. Falls back to 'Untitled deal' when undefined. */
  conversationTitle: string | undefined;

  /**
   * null  → no chip; RIGHT cluster renders <HeroMetricsHeader> (DIV-B fallback
   *         for legacy models without a Signal configuration).
   * non-null → centralized chip via VARIANT_* maps; RIGHT cluster renders
   *         <HeroReturns> when showHeroReturns is true.
   */
  signalVariant: SignalVariant | null;

  /** Mono-uppercase subline text (e.g. "175 UNITS · 350 5TH AVE · NEW DEVELOPMENT"). */
  subline: string;

  /**
   * When true the RIGHT cluster is rendered:
   *   - signalVariant non-null → <HeroReturns>
   *   - signalVariant null     → <HeroMetricsHeader> (DIV-B)
   * When false the RIGHT cluster is OMITTED entirely (Workbench AC; spec WS1).
   */
  showHeroReturns: boolean;

  // --- RIGHT cluster: HeroReturns props (required when showHeroReturns + non-null variant) ---

  /** Resolver context threaded to <HeroReturns>. */
  heroContext?: SignalFieldResolverContext;
  /** Model id for <HeroReturns> schema resolution. */
  heroModelId?: string;
  /** Model version forwarded to <HeroReturns>. */
  heroModelVersion?: string;
  /** Metric history for <HeroReturns> sparklines. */
  heroMetricHistory?: Record<string, MetricHistoryEntry[]> | null;

  // --- RIGHT cluster: HeroMetricsHeader props (required when showHeroReturns + null variant / DIV-B) ---

  /** Conversation id for <HeroMetricsHeader> (DIV-B fallback). */
  heroConversationId?: string;
  /** Model schema for <HeroMetricsHeader> (DIV-B fallback). */
  heroModelSchema?: ModelSchema | null;
  /** Live metrics map for <HeroMetricsHeader> (DIV-B fallback). */
  heroLiveMetrics?: Record<string, number>;

  // --- ADR 0006 — pill slot ---

  /**
   * Empty in Phase 3. Phase 4 fills with the 3-state active-version pill
   * (◌/▸/▲ glyph + drift classes via useActiveVersionDrift).
   * Rendered LEFT cluster, after chip, before subline.
   */
  pillSlot?: ReactNode;

  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DealHeaderStrip({
  conversationTitle,
  signalVariant,
  subline,
  showHeroReturns,
  heroContext,
  heroModelId,
  heroModelVersion,
  heroMetricHistory,
  heroConversationId,
  heroModelSchema,
  heroLiveMetrics,
  pillSlot,
  className,
}: DealHeaderStripProps) {
  return (
    <header
      data-testid="deal-header-strip"
      className={clsx(
        // Axis A (macro structure) + Axis B (density) — VERBATIM row class
        // from research/phase-3.md § 4 WS1 (byte-identical to signal-title-strip).
        'flex items-end justify-between gap-8 px-8 pt-2 pb-1.5 border-b border-panel-border flex-shrink-0',
        className
      )}
    >
      {/* LEFT cluster */}
      <div className="flex flex-col min-w-0 flex-shrink relative">
        <div className="flex items-center gap-2.5">
          {/* h1 — Axis C (typography: DM Sans 20px/600 tracking-[-0.2px]) + Axis D (color: text-text-bright) */}
          <h1 className="text-[20px] font-body font-semibold tracking-[-0.2px] text-text-bright truncate leading-tight">
            {conversationTitle || 'Untitled deal'}
          </h1>

          {/* Accent pulse dot — Axis E (chrome); box-shadow literal-hex exemption
              (@design-token resolves --color-accent = #00c853; audit DOES NOT cover
              builder/ root — but kept annotated for forward-compat). */}
          <span
            aria-hidden="true"
            className="w-1.5 h-1.5 rounded-full bg-accent shrink-0 animate-pulse"
            style={{
              boxShadow:
                '0 0 6px #00c853' /* @design-token resolves --color-accent */,
            }}
          />

          {/* CENTRALIZED signal-variant chip — only renders when signalVariant !== null.
              Axis C (chip: JetBrains Mono 8.5px bold tracking-[1px] uppercase) +
              Axis D (color: text-accent op-asset / text-blue new-dev) +
              Axis E (chip glow via VARIANT_CHIP_STYLE).
              Static Record lookup — NO class interpolation (F03). */}
          {signalVariant && (
            <span
              data-testid="signal-variant-chip"
              data-variant={signalVariant}
              aria-label={`Signal variant: ${VARIANT_LABEL[
                signalVariant
              ].toLowerCase()}`}
              className={VARIANT_CHIP_CLASS[signalVariant]}
              style={VARIANT_CHIP_STYLE[signalVariant]}
            >
              {VARIANT_LABEL[signalVariant]}
            </span>
          )}
        </div>

        {/* Subline — Axis C (mono 10px tracking-[1.2px] uppercase) + Axis D (text-text-muted) */}
        <div className="text-[10px] font-mono uppercase tracking-[1.2px] text-text-muted truncate mt-1">
          {subline}
        </div>

        {/* ADR 0006 — pill slot. Moved OUT of the title row onto its OWN row
            (M3 fix, 2026-06-08). In the drifted state the active-version pill
            grows wide (ACTIVE · v · UNSAVED CHANGES + Restore + Create); inline
            in the title row it shared the same `justify-between` header track as
            the RIGHT-cluster hero metrics (<HeroReturns>/<HeroMetricsHeader>) and
            collided/overprinted them as width shrank — and near-touched even at
            1440 (~41px gap). Its own row keeps it in the LEFT column, never
            competing horizontally with the metrics strip at any width. `flex-wrap`
            lets multiple controls wrap; `empty:hidden` keeps the row invisible
            (no extra gap) when pillSlot is empty/undefined. data-testid preserved
            (ADR 0006 forward-compat lock). */}
        <div
          data-testid="active-version-pill-slot"
          className="flex flex-wrap items-center gap-1.5 mt-1.5 empty:hidden"
        >
          {pillSlot}
        </div>
      </div>

      {/* RIGHT cluster — conditional on showHeroReturns.
          • showHeroReturns=false → omitted entirely (Workbench AC; spec WS1).
          • signalVariant non-null → <HeroReturns> (Model/Signal tabs).
          • signalVariant null + showHeroReturns → <HeroMetricsHeader> (DIV-B fallback
            for legacy models without a Signal config — MUST NOT be dropped). */}
      {showHeroReturns && (
        <div className="flex-shrink-0">
          {signalVariant ? (
            <HeroReturns
              context={heroContext!}
              variant={signalVariant}
              modelId={heroModelId ?? ''}
              modelVersion={heroModelVersion}
              metricHistory={heroMetricHistory}
            />
          ) : (
            // DIV-B fallback — signalVariant===null → variant-agnostic HeroMetricsHeader.
            // research/phase-3.md § 8 DIV-B: "Extraction drops NO branch IF the shared
            // strip carries the null-fallback." This branch is the regression lock.
            <HeroMetricsHeader
              conversationId={heroConversationId ?? ''}
              modelSchema={heroModelSchema ?? null}
              liveMetrics={heroLiveMetrics ?? {}}
              metricHistory={heroMetricHistory ?? null}
              className="border-b-0 px-0 py-0 shrink-0"
            />
          )}
        </div>
      )}
    </header>
  );
}
